import { useMemo, useCallback, useState } from "react";
import { Link, useNavigate } from "react-router";
import { shallow } from "zustand/shallow";
import LayerPartnerAgree from "@features/auth/component/userInfo/afterLogin/partnerDialog/LayerPartnerAgree";
import Heading from "@core/components/title/Heading";
import ICafeList from "@features/account/components/iCafe/ICafeList";
import { Button } from "@core/components/ui";
import CustomAlert from "@core/components/dialog/CustomAlert";
import PaymentType from "@core/components/payment/PaymentType";
import { useDialog } from "@core/components/dialog/hooks/useDialog";
import { useAccountStore } from "@core/stores/accountStore";
import { useAuthStore } from "@core/stores/authStore";
import { useUiStore } from "@core/stores/uiStore";
import IconPartner from "@/assets/svg/userInfo/icon-partner.svg?react";
import { ChevronRight } from "lucide-react";
import { cn } from "@core/utils/domUtil";
import { ID_DOMAIN } from "@core/constants/service";

// 파트너 PC방 가입 완료
function PartnerPcbang({ registerDate }: { registerDate: string }) {
  const { isOpen, toggle, open } = useDialog({ type: "dialog", name: "partnerAgreeComplete" });

  const clickHandler = useCallback(() => {
    open();
  }, [open]);

  return (
    <>
      <Button className={cn("bg-point-600 flex h-[48px] w-[100px] flex-col items-center justify-center rounded-md text-[12px]/[16px] text-white")} onClick={clickHandler}>
        <IconPartner className="w-[30px]" />
        파트너 PC방
      </Button>
      <LayerPartnerAgree open={isOpen} onOpenChange={toggle} registerDate={registerDate} isPartner={true} />
    </>
  );
}

// 파트너 PC방 미가입
function NotPartnerPcbang({ iCafeId, smartICafeJoinable }: { iCafeId: string; smartICafeJoinable: boolean }) {
  const { isOpen, toggle, open } = useDialog({ type: "dialog", name: "partnerAgree" });
  const navigate = useNavigate();

  const clickHandler = useCallback(() => {
    // CS에서 파트너 가입 권유한 PC방일 경우 - 파트너 PC방 가입 가능
    if (smartICafeJoinable) {
      open();
      return;
    }

    // 그 외 PC방 - 파트너 PC방 소개 페이지로 이동
    navigate(`/service/partner`);
  }, [open, smartICafeJoinable, navigate]);

  return (
    <>
      <Button variant="bgPurple" size="small" className={cn("h-[48px] w-[100px] text-[12px]/[16px]")} onClick={clickHandler}>
        <span>
          파트너
          <br />
          PC방 {smartICafeJoinable ? "가입" : "안내"}
        </span>
        <ChevronRight />
      </Button>
      <LayerPartnerAgree open={isOpen} onOpenChange={toggle} iCafeId={iCafeId} isPartner={false} />
    </>
  );
}

// 사업장 정보 등록
function RegisterCafe() {
  return (
    <Link to="/account/cafe" className="mt-[22px] flex h-[50px] w-full cursor-pointer items-center justify-between rounded-lg border border-gray-200 bg-white px-[20px]">
      사업장 정보 등록 <ChevronRight />
    </Link>
  );
}

export default function PlayncUser() {
  const account = useAccountStore((state) => state.account);
  const { primaryICafeId, iCafeList, loginICafeInfo } = useAuthStore(
    (state) => ({
      primaryICafeId: state.primaryICafeId,
      iCafeList: state.iCafeList,
      loginICafeInfo: state.loginICafeInfo,
    }),
    shallow,
  );
  const { gCoin = 0, paymentType = 0, recurringStatus = false, smartICafe = false, smartICafeJoinable = false } = loginICafeInfo ?? {};

  const [isOpenCheckRealName, setIsOpenCheckRealName] = useState(false);
  const checkRealName = useMemo(() => account?.playncUserInfo.checkRealName, [account]);
  const network = useUiStore((state) => state.network);

  const hasICafeList = useMemo(() => {
    return Array.isArray(iCafeList) && iCafeList?.length > 0;
  }, [iCafeList]);

  const openAutoPayment = useCallback(() => {
    // 본인인증 안된 계정은 본인인증 앨럿 띄운 후 인증 페이지로 이동
    if (!checkRealName) {
      setIsOpenCheckRealName(true);
      return;
    }

    window.open("/product/charge/payment", "_blank", "width=390,height=800");
  }, []);

  return (
    <>
      <div className="flex w-full items-center justify-between">
        <Heading level={1} className="text-lg font-normal">
          안녕하세요! <br /> <span className="font-bold">{account?.ncfzPlayncUserInfo?.personName ?? ""} 사장님</span>
        </Heading>
        {smartICafe ? <PartnerPcbang registerDate={loginICafeInfo?.smartICafeRegisterDate ?? ""} /> : primaryICafeId ? <NotPartnerPcbang iCafeId={primaryICafeId} smartICafeJoinable={smartICafeJoinable} /> : <></>}
      </div>
      {hasICafeList ? <ICafeList className={cn("mt-[20px] w-full")} /> : <RegisterCafe />}
      <div className="mt-[10px] w-full">
        <dl className="bg-point-100 flex h-[50px] w-full items-center justify-between rounded-lg px-[20px]">
          <dt className="text-md font-medium">잔여 G코인</dt>
          <dd className="text-point-600 text-[20px] font-bold">{Intl.NumberFormat("ko-KR").format(gCoin ?? 0)}</dd>
        </dl>
      </div>
      <div className="mt-[18px] w-full">
        <div className="flex w-full items-center justify-between">
          <dl className="text-md flex w-full items-center gap-[4px]">
            <dt>자동결제 : </dt>
            <dd className="text-point-600 font-bold">{recurringStatus ? <span className="text-success">ON</span> : <span className="text-gray-400">OFF</span>}</dd>
          </dl>
          {!recurringStatus && (
            <Button variant="borderPoint200" size="small" className={cn("h-[32px] px-[12px] text-[12px]")} onClick={openAutoPayment}>
              자동결제 신청 <ChevronRight />
            </Button>
          )}
        </div>
        <div className="mb-[16px] w-full">
          <dl className="text-md flex w-full items-center gap-[4px]">
            <dt>결제 수단 : </dt>
            <dd className="font-medium">
              <PaymentType code={paymentType!} useDefaultDash={true} />
            </dd>
          </dl>
        </div>
      </div>

      {/* 본인 인증 앨럿 */}
      <CustomAlert
        name="checkRealName"
        isOpen={isOpenCheckRealName}
        onOpenChange={setIsOpenCheckRealName}
        onClick={() => {
          location.href = `${ID_DOMAIN[network]}/identify/self/intro?returnUrl=${window.location.href}&cancelUrl=${window.location.href}`;
        }}
      >
        <div>
          <p>
            본인인증 후 충전 가능합니다.
            <br />
            안전한 충전을 위해 본인인증 페이지로 이동합니다.
          </p>
        </div>
      </CustomAlert>
    </>
  );
}
